import {Action, getModule, Module, Mutation, VuexModule} from "vuex-module-decorators";
import store from "@/store";
import {DistrictState} from "@/modules/regionsv2/district/state";
import {IdMapper} from "@/interfaces/state/base";
import {District} from "@/interfaces/entity/district";
import {ChangeStatusProp, PaginationMeta} from "@/interfaces/common";
import {DistrictParams} from "@/modules/regionsv2/district/params";
import {
    createDistrictV21, deleteDistrictV21,
    editDistrictV21, listDistrictV21,
} from "@/modules/regionsv2/district/services";
import {union, cloneDeep, set} from "lodash";
import {getDistrictBySlugsAndProvinceIds} from "@/modules/regions/services";

@Module({ dynamic: true, name: "district", store })
class DistrictModule extends VuexModule implements DistrictState {
    data: IdMapper<District> = {};
    ids: number[] = [];
    meta: PaginationMeta = new PaginationMeta();
    params: DistrictParams = new DistrictParams();
    mapIdsByProvinceId: IdMapper<Array<number>> = {};
    cachedData: IdMapper<District> = {};
    loadingStatus: IdMapper<LoadingStatus> = {};

    get districts() {
        return this.ids.map((id) => this.data[id]);
    }

    @Mutation
    private MAP_DISTRICTS_V21(payload: any) {
        const data: IdMapper<District> = cloneDeep(this.data);
        this.meta = payload.meta;
        this.ids = payload.datas.map((district: District) => {
            data[district.id] = {
                ...district,
                isDeleting: false,
                isSaving: false,
                editable: false
            } as any;
            return district.id;
        });
        this.data = data;
        this.cachedData = cloneDeep(data);
    }

    @Mutation
    private MAP_DISTRICT_BY_PROVINCE_V21(payload: District[]) {
        const data: IdMapper<District> = cloneDeep(this.data);
        const mapIdsByProvinceId = cloneDeep(this.mapIdsByProvinceId);
        payload.forEach((district: District) => {
            data[district.id] = district;
            mapIdsByProvinceId[district.provinceId] = union(mapIdsByProvinceId[district.provinceId], [district.id]);
        });
        this.data = data;
        this.mapIdsByProvinceId = mapIdsByProvinceId;
    }

    @Mutation
    private UPDATE_DISTRICT_PARAMS_V21(params: any) {
        this.params = { ...params };
    }

    @Mutation
    private UPDATE_DATA_DISTRICT_V21({ key, value }: any) {
        this.data[key] = value;
    }

    @Mutation
    private UPDATE_CACHED_DATA_DISTRICT_V21({ key, value }: any) {
        this.cachedData[key] = value;
    }

    @Mutation
    private SET_DISTRICT_V21(payload: District) {
        this.ids = union([payload.id], this.ids);
        this.data = { ...this.data, [payload.id]: payload }
    }

    @Mutation
    private DELETE_DISTRICT_V21(payload: District) {
        this.ids = this.ids.filter((id) => id != payload.id);
        delete this.data[payload.id];
    }

    @Mutation
    private CHANGE_LOADING_STATUS_DISTRICT_V21({key, value }: ChangeStatusProp) {
        set(this.loadingStatus, key, value);
    }

    @Action({ rawError: true })
    public async listDistrictsV21(params: any = {}) {
        const { data } = await listDistrictV21(params);
        this.MAP_DISTRICTS_V21(data);
        return data.datas;
    }

    @Action({ rawError: true })
    public async listDistrictsByProvinceV21(provinceId: number) {
        if (!this.loadingStatus[provinceId] || this.loadingStatus[provinceId] == 'waiting') {
            const params = { ...new DistrictParams(), provinceId, limit: 1000 };
            this.CHANGE_LOADING_STATUS_DISTRICT_V21({ key: `${provinceId}`, value: "loading" });
            const { data } = await listDistrictV21(params);
            this.CHANGE_LOADING_STATUS_DISTRICT_V21({ key: `${provinceId}`, value: "loaded" });
            this.MAP_DISTRICT_BY_PROVINCE_V21(data.datas);
            return data.datas;
        }
    }

    @Action({ rawError: true })
    public async createDistrictV21(payload: any) {
        const { data } = await createDistrictV21(payload);
        this.SET_DISTRICT_V21({ ...data.data, isDeleting: false, isSaving: false, editable: false });
        return data.data;
    }

    @Action({ rawError: true })
    public async editDistrictV21(payload: any) {
        const { data } = await editDistrictV21(payload);
        this.SET_DISTRICT_V21(data.data);
        return data.data;
    }

    @Action({ rawError: true })
    public async deleteDistrictV21(id: number) {
        const { data } = await deleteDistrictV21(id);
        this.DELETE_DISTRICT_V21(data.data);
        return data.data;
    }

    @Action({ rawError: true })
    public async paramsChangeDistrictV21(params: any) {
        this.UPDATE_DISTRICT_PARAMS_V21(params);
        await this.listDistrictsV21(params);
    }

    @Action({ rawError: true })
    public updateDataDistrictV21({ key, value }: any) {
        this.UPDATE_DATA_DISTRICT_V21({ key, value });
    }

    @Action({ rawError: true })
    public updateCachedDataDistrictV21({ key, value }: any) {
        this.UPDATE_CACHED_DATA_DISTRICT_V21({ key, value });
    }

    @Action({ rawError: true })
    public async getDistrictsForExcelV21({ provinceIds, districtSlugs }: any) {
        const { data } = await getDistrictBySlugsAndProvinceIds(provinceIds, districtSlugs);
        this.MAP_DISTRICT_BY_PROVINCE_V21(data.datas);
        return data.datas;
    }
}

export const DistrictStore = getModule(DistrictModule)
